/**
 * Lightweight session context for `cai session --auto`.
 *
 * This is the hook-mode counterpart to the full `runSession`. It runs on
 * EVERY user prompt via Claude Code's UserPromptSubmit hook, so it has
 * a hard performance budget (<100ms) and a hard simplicity budget
 * (no file dialogs, no clipboard, no LLM calls).
 *
 * Output format follows Claude Code's hook spec:
 *   {
 *     "hookSpecificOutput": {
 *       "hookEventName": "UserPromptSubmit",
 *       "additionalContext": "<short string injected before the user prompt>"
 *     }
 *   }
 *
 * If anything goes wrong (no .cai/, no git, etc.), the function returns
 * an empty object so the hook becomes a silent no-op. NEVER blocks the prompt.
 */
import { execFileSync } from "node:child_process";
import { existsSync, readFileSync, readdirSync } from "node:fs";
import { join } from "node:path";

interface SessionAutoOptions {
  projectRoot: string;
  /** Override the default 7-day window for telemetry hot files */
  hotFilesDays?: number;
  /** Max chars in the additionalContext output */
  maxChars?: number;
}

const DEFAULT_MAX_CHARS = 800;

function git(cwd: string, args: string[]): string {
  try {
    return execFileSync("git", args, {
      cwd,
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
      maxBuffer: 1024 * 1024,
      timeout: 500,
    });
  } catch {
    return "";
  }
}

interface AutoContext {
  changedFiles: string[];
  recentCommits: string[];
  hotFiles: string[];
  driftSummary: string | null;
  /** Names (no .md) of documented context files, ordered by stability. */
  contextFiles?: string[];
}

// Same stability order the router uses (context-router.ts) — decisions/stack are
// the most stable, patterns/unknown last. Keeps the inventory line deterministic.
const STABILITY_ORDER = ["decisions", "stack", "architecture", "conventions", "setup"];

/** List documented context files under .cai/context, ordered by stability. */
function readContextFiles(projectRoot: string): string[] {
  const contextDir = join(projectRoot, ".cai", "context");
  if (!existsSync(contextDir)) return [];
  let names: string[];
  try {
    names = readdirSync(contextDir)
      .filter((f) => f.endsWith(".md"))
      .map((f) => f.slice(0, -3));
  } catch {
    return [];
  }
  const orderOf = (name: string) => {
    const idx = STABILITY_ORDER.indexOf(name);
    return idx === -1 ? STABILITY_ORDER.length : idx;
  };
  return names.sort((a, b) => orderOf(a) - orderOf(b) || a.localeCompare(b));
}

export function buildAutoContext(opts: SessionAutoOptions): AutoContext {
  const { projectRoot } = opts;

  // Uncommitted changed files (cheap)
  const statusOut = git(projectRoot, ["status", "--porcelain"]);
  const changedFiles: string[] = [];
  for (const line of statusOut.split("\n")) {
    if (!line.trim()) continue;
    const file = line.slice(3).trim();
    if (file.startsWith(".cai/") || file.startsWith(".claude/")) continue;
    changedFiles.push(file);
  }

  // Last 3 commit subjects (cheap)
  const logOut = git(projectRoot, ["log", "-3", "--format=%h %s"]);
  const recentCommits = logOut.split("\n").filter(Boolean);

  // Hot files from telemetry (no parsing, just top names)
  const hotFiles = readHotFiles(projectRoot, opts.hotFilesDays ?? 7);

  // Latest drift score from history (instant — single tail of jsonl)
  const driftSummary = readLatestDrift(projectRoot);

  // Documented context files — so a fresh window learns what exists (P0)
  const contextFiles = readContextFiles(projectRoot);

  return { changedFiles, recentCommits, hotFiles, driftSummary, contextFiles };
}

function readHotFiles(projectRoot: string, days: number): string[] {
  const path = join(projectRoot, ".cai", ".cache", "queries.jsonl");
  if (!existsSync(path)) return [];

  try {
    const raw = readFileSync(path, "utf8");
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    const counts = new Map<string, number>();

    for (const line of raw.split("\n")) {
      if (!line) continue;
      try {
        const parsed = JSON.parse(line) as { ts: number; file?: string };
        if (parsed.ts < cutoff || !parsed.file) continue;
        counts.set(parsed.file, (counts.get(parsed.file) ?? 0) + 1);
      } catch {
        // skip
      }
    }

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([f]) => f);
  } catch {
    return [];
  }
}

function readLatestDrift(projectRoot: string): string | null {
  const path = join(projectRoot, ".cai", ".cache", "drift-history.jsonl");
  if (!existsSync(path)) return null;

  try {
    const raw = readFileSync(path, "utf8");
    const lines = raw.split("\n").filter(Boolean);
    if (lines.length === 0) return null;
    const last = JSON.parse(lines[lines.length - 1]) as {
      score: number;
      weightedScore?: number;
      errors: number;
      warnings: number;
    };
    const scoreLabel = last.weightedScore !== undefined ? `${last.score} (weighted ${last.weightedScore})` : `${last.score}`;
    return `drift: ${scoreLabel}/100, ${last.errors}E ${last.warnings}W`;
  } catch {
    return null;
  }
}

/** Render the auto context as a compact text block, capped to maxChars. */
export function renderAutoContext(ctx: AutoContext, maxChars = DEFAULT_MAX_CHARS): string {
  const parts: string[] = [];

  // Prepend the standing-context map: a fresh window otherwise never learns which
  // context files exist. Goes FIRST because the cap below slices the tail — an
  // appended line would be the first thing cut.
  if (ctx.contextFiles && ctx.contextFiles.length > 0) {
    parts.push(
      `[cai] context available: ${ctx.contextFiles.join(", ")} — start with cai_list_context or read .cai/ROUTER.md`
    );
  }

  if (ctx.driftSummary) {
    parts.push(`[cai] ${ctx.driftSummary}`);
  }

  if (ctx.changedFiles.length > 0) {
    const head = ctx.changedFiles.slice(0, 8);
    const more = ctx.changedFiles.length > 8 ? ` (+${ctx.changedFiles.length - 8} more)` : "";
    parts.push(`[cai] uncommitted: ${head.join(", ")}${more}`);
  }

  if (ctx.recentCommits.length > 0) {
    parts.push(`[cai] recent commits:\n  ${ctx.recentCommits.join("\n  ")}`);
  }

  if (ctx.hotFiles.length > 0) {
    parts.push(`[cai] hot context files (last 7d): ${ctx.hotFiles.join(", ")}`);
  }

  if (parts.length === 0) return "";

  const full = parts.join("\n");
  if (full.length <= maxChars) return full;
  return full.slice(0, maxChars - 1) + "…";
}

/** Build the JSON object that gets written to stdout for Claude Code. */
export function renderHookOutput(text: string): Record<string, unknown> {
  if (!text) return {};
  return {
    hookSpecificOutput: {
      hookEventName: "UserPromptSubmit",
      additionalContext: text,
    },
  };
}

/**
 * The whole `cai session --auto` flow as one call.
 * Returns the JSON string to write to stdout. Always returns valid JSON,
 * never throws.
 */
export function runSessionAuto(projectRoot: string): string {
  try {
    if (!existsSync(join(projectRoot, ".cai")) && !existsSync(join(projectRoot, ".context-condensing"))) {
      return "{}";
    }
    const ctx = buildAutoContext({ projectRoot });
    const text = renderAutoContext(ctx);
    return JSON.stringify(renderHookOutput(text));
  } catch {
    return "{}";
  }
}
